import React, { Component, Fragment } from "react";
import { Col } from "react-bootstrap";
import { connect } from "react-redux";
import { compose } from "redux";
import { Field, Form, withFormik } from "formik";
import { func, string, bool, shape } from "prop-types";
import * as Yup from "yup";
import TextField from "pages/_components/fields/TextField";
import Notification from "pages/_components/Notification";

import { actions as enrollmentActions, selectors as enrollmentSelectors } from "reducers/enrollment";
import { selectors as onboardingSelectors } from "reducers/onboarding";
import { actions as loginActions } from "reducers/login";
import * as configUtils from "util/config";
import * as i18nUtils from "util/i18n";

import Button from "pages/_components/Button";
import Container from "pages/_components/Container";
import Head from "pages/_components/Head";
import I18n from "pages/_components/I18n";
import PasswordRules from "pages/_components/PasswordRules";
import Credential from "pages/_components/fields/credentials/Credential";
import withExchangeToken from "pages/_components/withExchangeToken";
import OnboardingStepper, { orientations } from "pages/onboarding/_components/OnboardingStepper";
import { Mixpanel } from "util/clickstreaming";
import EnrollmentStepper from "./_components/EnrollmentStepper";

const FORM_ID = "enrollment.step3";

class Step3Part1 extends Component {
    componentDidMount() {
        const { dispatch, invitationCode, exchangeToken } = this.props;

        if (!invitationCode) {
            dispatch(enrollmentActions.goToStep0());
        }

        dispatch(enrollmentActions.requestSecuritySeals(exchangeToken));
    }

    onHeaderClose = () => {
        const { dispatch } = this.props;

        dispatch(loginActions.reset());
    };

    render() {
        const { fetching, isMobile, documentData } = this.props;
        const maxLength = configUtils.getInteger("core.password.maxLength");
        const step = 9;
        return (
            <>
                <Notification scopeToShow="RegisterStep3Part1" />

                {isMobile && <Head title="onboarding.step.9.title" onClose={isMobile && this.onHeaderClose} />}

                <main className="main-container">
                    {!isMobile && (
                        <Container>
                            <Col xs={12} md={8} mdOffset={4}>
                                <Head title="onboarding.step.9.title" onClose={isMobile && this.onHeaderClose} />
                            </Col>
                        </Container>
                    )}
                    <Container>
                        <Col xs={12} md={4}>
                            {!isMobile && documentData.firstName && (
                                <OnboardingStepper currentStep={step} className="onboarding-steps" />
                            )}
                            {!(isMobile || documentData.firstName) && (
                                <EnrollmentStepper currentStep={3} className="onboarding-steps" />
                            )}
                        </Col>
                        <Col xs={12} md={8}>
                            <Form className="above-the-fold">
                                {isMobile && (
                                    <Container className="container--layout items-center">
                                        <Col className="col-12 mt-5 mb-8">
                                            {documentData.firstName && (
                                                <OnboardingStepper
                                                    currentStep={step}
                                                    orientation={orientations.horizontal}
                                                />
                                            )}
                                            {!documentData.firstName && (
                                                <EnrollmentStepper
                                                    currentStep={3}
                                                    orientation={orientations.horizontal}
                                                />
                                            )}
                                        </Col>
                                    </Container>
                                )}

                                <I18n
                                    component="p"
                                    componentProps={{ className: "mb-8" }}
                                    id="onboarding.step.9.info"
                                />

                                <Field
                                    component={TextField}
                                    hidePlaceholder
                                    idForm={FORM_ID}
                                    name="username"
                                    showStrength
                                    maxLength={maxLength}
                                />
                                <Field
                                    component={Credential}
                                    hidePlaceholder
                                    idForm={FORM_ID}
                                    name="password"
                                    showStrength
                                    maxLength={maxLength}
                                />
                                <PasswordRules>
                                    <I18n
                                        id={`${FORM_ID}.passwordRules.rule1`}
                                        MIN_LENGTH={configUtils.get("core.password.minLength")}
                                        MAX_LENGTH={configUtils.get("core.password.maxLength")}
                                    />
                                    <I18n id={`${FORM_ID}.passwordRules.rule2`} />
                                    <I18n id={`${FORM_ID}.passwordRules.rule3`} />
                                    <I18n id={`${FORM_ID}.passwordRules.rule4`} />
                                </PasswordRules>
                                <Field
                                    component={Credential}
                                    hidePlaceholder
                                    idForm={FORM_ID}
                                    name="passwordConfirmation"
                                    maxLength={maxLength}
                                />

                                <Button
                                    className="mt-8"
                                    label="global.continue"
                                    type="submit"
                                    bsStyle="primary"
                                    loading={fetching}
                                    block
                                />

                                {/* <Container className="container--layout items-center"></Container> */}
                            </Form>
                        </Col>
                    </Container>
                </main>
            </>
        );
    }
}

const mapStateToProps = (state) => ({
    fetching: enrollmentSelectors.getFetching(state),
    exchangeToken: enrollmentSelectors.getExchangeToken(state),
    invitation: enrollmentSelectors.getInvitation(state),
    invitationCode: enrollmentSelectors.getInvitationCode(state),
    documentData: onboardingSelectors.getDocumentData(state),
});

Step3Part1.propTypes = {
    dispatch: func.isRequired,
    exchangeToken: string.isRequired,
    invitationCode: string.isRequired,
    isMobile: bool.isRequired,
    documentData: shape({
        firstName: string.isRequired,
    }).isRequired,
    fetching: bool,
};

Step3Part1.defaultProps = {
    fetching: false,
};

export default compose(
    connect(mapStateToProps),
    withFormik({
        validateOnChange: false,
        validateOnBlur: false,
        mapPropsToValues: () => ({ password: "", passwordConfirmation: "" }),
        validationSchema: () =>
            Yup.lazy((values) =>
                Yup.object().shape({
                    password: Yup.string()
                        .required(i18nUtils.get("enrollment.step3.password.error.empty"))
                        .min(
                            configUtils.getInteger("core.password.minLength"),
                            i18nUtils.get("enrollment.step3.password.error.unfulfilledRules"),
                        )
                        .max(
                            configUtils.getInteger("core.password.maxLength"),
                            i18nUtils.get("enrollment.step3.password.error.unfulfilledRules"),
                        )
                        .matches(
                            new RegExp(configUtils.get("core.userconfiguration.password.pattern"), "g"),
                            i18nUtils.get("enrollment.step3.password.error.unfulfilledRules"),
                        )
                        .oneOf(
                            [values.passwordConfirmation],
                            i18nUtils.get("enrollment.step3.password.error.passwordConfirmationMustMatch"),
                        ),
                    passwordConfirmation: Yup.string()
                        .required(i18nUtils.get("enrollment.step3.passwordConfirmation.error.empty"))
                        .min(
                            configUtils.getInteger("core.password.minLength"),
                            i18nUtils.get("enrollment.step3.passwordConfirmation.error.unfulfilledRules"),
                        )
                        .max(
                            configUtils.getInteger("core.password.maxLength"),
                            i18nUtils.get("enrollment.step3.passwordConfirmation.error.unfulfilledRules"),
                        )
                        .matches(
                            new RegExp(configUtils.get("core.userconfiguration.password.pattern"), "g"),
                            i18nUtils.get("enrollment.step3.passwordConfirmation.error.unfulfilledRules"),
                        )
                        .oneOf(
                            [values.password],
                            i18nUtils.get("enrollment.step3.passwordConfirmation.error.passwordMustMatch"),
                        ),
                    username: Yup.string()
                        .required(i18nUtils.get("enrollment.step3.username.error.empty"))
                        .min(
                            configUtils.getInteger("core.password.minLength"),
                            i18nUtils.get("enrollment.step3.username.error.unfulfilledRules"),
                        )
                        .max(
                            configUtils.getInteger("core.password.maxLength"),
                            i18nUtils.get("enrollment.step3.username.error.unfulfilledRules"),
                        ),
                }),
            ),
        handleSubmit: ({ password, passwordConfirmation, username }, formikBag) => {
            const { dispatch, exchangeToken } = formikBag.props;
            dispatch(enrollmentActions.requestAccountData(password, passwordConfirmation, username, exchangeToken));
            Mixpanel.track(`${FORM_ID}.password`);
            // dispatch(push("/enrollment/step3part3"));
        },
    }),
)(withExchangeToken(Step3Part1));
